import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import {
  AuditEntityTypeEnum,
  CompanyLocationRampDetailOutDTO,
  CompanyLocationsDetailOutDTO,
  DayEnum,
  OpeningModeEnum,
  PermissionTypesEnum,
} from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import Badge from '@components/ringil3/Badges/Badge.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import EntityAuditLog, { EntityAuditLogHandle } from '@components/RingilComponents/audit/EntityAuditLog.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import AddEditRamp from '@app/pages/customer/LocalityList/sidebars/AddEditRamp.tsx';
import SvgInfo from '@icons/info.svg?react';
import SvgArrivalTruck from '@icons/arrival-truck.svg?react';
import SvgPencil from '@icons/pencil-inverted.svg?react';

interface ComponentProps {
  locationId: string;
  rampId: string;
}

const WEEK_DAYS = [DayEnum.MONDAY, DayEnum.TUESDAY, DayEnum.WEDNESDAY, DayEnum.THURSDAY, DayEnum.FRIDAY, DayEnum.SATURDAY, DayEnum.SUNDAY];

const titleCaseDay = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const RampDetail: React.FC<ComponentProps> = ({ locationId, rampId }) => {
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();
  const { getRampDetailForLocation, getCompanyLocationById } = useContext(ApiContext);

  const [data, setData] = useState<CompanyLocationRampDetailOutDTO>();
  // Parent location: supplies the partner-location edit gate (mirrors RampsSection) and the breadcrumb name.
  const [location, setLocation] = useState<CompanyLocationsDetailOutDTO>();
  const [activeTab, setActiveTab] = useState(0);
  const [editVisible, setEditVisible] = useState(false);
  const auditLogRef = useRef<EntityAuditLogHandle>(null);

  // Ramp audit shares the location audit read permission (RD-2752).
  const showAudit = useSecuredFlag('ramp-detail-audit', {
    kind: 'visibility',
    description: 'Ramp change history / audit log',
    permissions: [PermissionTypesEnum.PERMISSION_LOCATION_DETAIL_AUDITLOG_READ],
  });

  const load = () => {
    getRampDetailForLocation(locationId, rampId, d => {
      setData(d);
      auditLogRef.current?.reload();
    });
    getCompanyLocationById(locationId, setLocation);
  };

  useEffect(() => {
    load();
  }, [locationId, rampId]);

  if (isNullOrUndefined(data)) return null;

  const editEnabled = !!location && !location.isPartnerLocation;

  const tabs: TabProps[] = [{ label: tr('LocalityDetail.ramp', 'Ramp'), active: activeTab === 0, anchorId: 'ramp-detail', icon: <SvgArrivalTruck /> }];
  // History is a switch tab (no anchorId), so scroll-spy leaves it selected while the audit panel replaces the sections.
  const historyTabIndex = tabs.length;
  if (showAudit.granted) {
    tabs.push({ label: tr('LocalityDetail.tabHistory', 'History'), active: activeTab === historyTabIndex, icon: <SvgInfo /> });
  }

  const renderDay = (day: DayEnum) => {
    const dayDef = data.openingHourDefinitions?.openingHoursDays?.find(d => d.day === day);
    if (isNullOrUndefined(dayDef) || dayDef.openingMode === OpeningModeEnum.CLOSED) {
      return <span className="text-content-tertiary">{tr('LocalityDetail.closed', 'Closed')}</span>;
    }
    if (dayDef.openingMode === OpeningModeEnum.OPEN) {
      return <span>{tr('LocalityDetail.allDay', 'non-stop')}</span>;
    }
    return (
      <div className="flex flex-col gap-1">
        {dayDef.openingHours.map((t, i) => (
          <span key={i}>
            {t.since}–{t.till}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <BreadCrumbComponent
        items={[
          { label: tr('LocalityDetail.locationList', 'Location list'), url: navigation.createNavigationLink(navigation.urlFunctions.createLocationsList()) },
          {
            label: location?.name ?? tr('LocalityDetail.locationDetail', 'Location detail'),
            url: navigation.createNavigationLink(navigation.urlFunctions.createLocationDetail(locationId)),
          },
          { label: data.name, url: null },
        ]}
      />

      <div>
        <Heading
          title={data.name}
          headingVariant={'h1'}
          buttons={editEnabled ? [<RingilButton key={'ramp-edit'} mode={'table'} icon={<SvgPencil />} onClick={() => setEditVisible(true)} dataTestId={'ramp-edit'} />] : undefined}
        />
      </div>

      <Ringil3BadgeRow>
        {data.isPrivate && (
          <COBadge variant={'filled'} type={'custom'} className={'bg-content-secondary text-white'} dataTestId={'ramp-flag-internal'}>
            {tr('LocalityDetail.rampInternal', 'Internal')}
          </COBadge>
        )}
        {data.isShowOnRampDashboard && (
          <COBadge variant={'filled'} type={'custom'} className={'bg-complementary-processing text-content-primary'} dataTestId={'ramp-flag-tv'}>
            {tr('LocalityDetail.rampInternalTv', 'Internal TV')}
          </COBadge>
        )}
        {data.isTemporaryClosed && (
          <COBadge variant={'filled'} type={'custom'} className={'bg-complementary-errorMedium text-content-primary'} dataTestId={'ramp-flag-closed'}>
            {tr('LocalityDetail.rampTemporarilyClosed', 'Temporarily closed')}
          </COBadge>
        )}
        <Badge label={tr('AddEditRamp.maxReservationCount', 'Bookings limit')} value={`${data.maxReservationCount ?? '—'}`} dataTestId={'ramp-badge-maxRes'} />
        <Badge label={tr('AddEditRamp.maxSkuCount', 'Handling units limit')} value={`${data.maxSkuAmount ?? '—'}`} dataTestId={'ramp-badge-maxSku'} />
        <Badge label={tr('AddEditRamp.weeklyMaxReservationCount', 'Weekly bookings limit')} value={`${data.weeklyMaxReservationCount ?? '—'}`} dataTestId={'ramp-badge-weeklyMaxRes'} />
        <Badge label={tr('AddEditRamp.weeklyMaxSkuCount', 'Weekly handling units limit')} value={`${data.weeklyMaxSkuAmount ?? '—'}`} dataTestId={'ramp-badge-weeklyMaxSku'} />
        <Badge label={tr('generic.timezone', 'Timezone')} value={data.timezoneId} dataTestId={'ramp-badge-timezone'} />
      </Ringil3BadgeRow>

      <Tabs tabs={tabs} onTabChange={setActiveTab} />

      {activeTab !== historyTabIndex && (
        <div id={'ramp-detail'} className="flex flex-col gap-4 scroll-mt-[72px]">
          <SectionPanel
            icon={<SvgArrivalTruck className={'w-6 h-6'} />}
            iconColor={'var(--content-tertiary)'}
            heading={tr('LocalityDetail.openingHours', 'Opening hours')}
            headerRight={<COTimezone timezoneId={data.timezoneId} dataTestId={'ramp-timezone'} />}>
            <div className="flex flex-col gap-2">
              {WEEK_DAYS.map(day => (
                <div key={day} className="flex flex-row gap-4">
                  <span className="w-32 text-content-secondary">{titleCaseDay(tr(`TimeIntervalComponent.days${day}`, titleCaseDay(day)))}</span>
                  {renderDay(day)}
                </div>
              ))}
            </div>
          </SectionPanel>
        </div>
      )}

      {showAudit.granted && activeTab === historyTabIndex && (
        <SecuredArea id="ramp-detail-audit" label="Audit log" description="History of all changes made to the ramp" visible={showAudit}>
          <EntityAuditLog ref={auditLogRef} entityType={AuditEntityTypeEnum.RAMP} entityId={data.rampId} hideHeader />
        </SecuredArea>
      )}

      <AddEditRamp
        visible={editVisible}
        onHide={() => setEditVisible(false)}
        onCompleted={() => {
          setEditVisible(false);
          load();
        }}
        locationId={locationId}
        timezoneId={data.timezoneId}
        editRampId={rampId}
      />
    </div>
  );
};

export default RampDetail;
